<script lang="ts">
  import { queryStock } from '../lib/analytics/stock';
  import Chart from '../lib/charts/Chart.svelte';
  import ChartCard from '../lib/charts/ChartCard.svelte';
  import { toCompositionOption, toTimeChartOption, type ChartMode } from '../lib/charts/options';
  import type { DataStore } from '../lib/data/store';
  import type { StockQuery, YearMonth } from '../lib/data/types';
  import FilterBar from '../lib/filters/FilterBar.svelte';
  import { formatCents, formatMonth, formatPercent, signedClass } from '../lib/presentation/format';
  import { meaningfulChange, selectedAccountMonths } from '../lib/presentation/insights';
  import { labelStockSeries } from '../lib/presentation/series';
  import type { AppState } from '../lib/state/app.svelte';
  import KpiCard from '../lib/ui/KpiCard.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';

  let { store, appState }: { store: DataStore; appState: AppState } = $props();
  let breakdown = $state<StockQuery['breakdown']>('none');
  let mode = $state<ChartMode>('area');
  let movingWindow = $state(0);

  function baseQuery(metric: StockQuery['metric'], requestedBreakdown = breakdown): StockQuery {
    return {
      metric,
      breakdown: requestedBreakdown,
      visiblePeriod: {
        from: appState.fromMonth ?? store.months[0]!,
        to: appState.toMonth ?? store.months.at(-1)!,
      },
      ...(appState.accountFilterActive ? { accountIds: new Set(appState.accountIds) } : {}),
      ...(movingWindow > 0
        ? { movingAverage: { window: movingWindow, mode: 'trailing' as const } }
        : {}),
    };
  }

  let query = $derived(baseQuery('closing-balance'));
  let trend = $derived(
    labelStockSeries(queryStock(store.core.account_month, store.months, query), query, store),
  );
  let rawQuery = $derived.by(() => {
    const value = baseQuery('closing-balance', 'none');
    const { movingAverage: _, ...withoutMovingAverage } = value;
    return withoutMovingAverage;
  });
  let rawTrend = $derived(queryStock(store.core.account_month, store.months, rawQuery));
  let deltaQuery = $derived.by(() => {
    const value = baseQuery('delta', 'none');
    const { movingAverage: _, ...withoutMovingAverage } = value;
    return withoutMovingAverage;
  });
  let deltas = $derived(
    labelStockSeries(
      queryStock(store.core.account_month, store.months, deltaQuery),
      deltaQuery,
      store,
    ),
  );
  let values = $derived(rawTrend.series[0]?.values ?? []);
  let current = $derived(values.at(-1) ?? 0);
  let previous = $derived(values.at(-2) ?? current);
  let monthDelta = $derived(current - previous);
  let finalMonth = $derived((rawTrend.buckets.at(-1) ?? store.months.at(-1))! as YearMonth);
  let finalRows = $derived(
    selectedAccountMonths(
      store,
      finalMonth,
      appState.accountFilterActive ? appState.accountIds : undefined,
    ),
  );
  let composition = $derived.by(() => {
    const groups = new Map<string, number>();
    for (const row of finalRows)
      groups.set(
        row.account_group_id,
        (groups.get(row.account_group_id) ?? 0) + row.closing_balance_cents,
      );
    return Array.from(groups, ([id, value]) => ({
      name: store.groupLabel(id),
      value,
      colorKey: `group:${id}`,
    })).sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  });
  let previousPeriodChange = $derived.by(() => {
    const count = rawTrend.buckets.length;
    const startIndex = store.months.indexOf((rawTrend.buckets[0] ?? finalMonth) as YearMonth);
    if (count === 0 || startIndex < count) return null;
    const from = store.months[startIndex - count];
    const to = store.months[startIndex - 1];
    if (!from || !to) return null;
    const previousQuery: StockQuery = { ...rawQuery, visiblePeriod: { from, to } };
    const result = queryStock(store.core.account_month, store.months, previousQuery);
    const periodValues = result.series[0]?.values ?? [];
    return (periodValues.at(-1) ?? 0) - (periodValues[0] ?? 0);
  });
</script>

<main id="main-content" class="page">
  <PageHeader
    title="Net worth"
    description="Leggi l'evoluzione del patrimonio, separa gruppi e conti e confronta periodi omogenei."
    state={appState}
  />
  <FilterBar state={appState} />

  <div class="kpi-grid">
    <KpiCard
      hero
      label={`Saldo finale · ${formatMonth(finalMonth)}`}
      value={formatCents(current)}
    />
    <KpiCard
      label="Variazione mensile"
      value={formatCents(monthDelta)}
      detail={formatPercent(meaningfulChange(current, previous))}
      tone={signedClass(monthDelta)}
    />
    <KpiCard
      label="Periodo selezionato"
      value={formatCents(current - (values[0] ?? current))}
      detail={`${rawTrend.buckets.length} mesi`}
      tone={signedClass(current - (values[0] ?? current))}
    />
    <KpiCard
      label="Periodo precedente"
      value={previousPeriodChange === null ? 'Non disponibile' : formatCents(previousPeriodChange)}
      detail="Stessa durata"
      tone={previousPeriodChange === null ? 'neutral' : signedClass(previousPeriodChange)}
    />
  </div>

  <div class="page-grid">
    <section class="card span-12">
      <div class="card-header">
        <div>
          <h2>Patrimonio nel tempo</h2>
          <p class="muted">La media mobile viene applicata alla serie già filtrata.</p>
        </div>
        <div class="chart-controls">
          <label
            >Media <select bind:value={movingWindow}
              ><option value={0}>Nessuna</option><option value={3}>3 mesi</option><option value={6}
                >6 mesi</option
              ><option value={12}>12 mesi</option></select
            ></label
          >
          <div class="segmented" aria-label="Suddivisione serie">
            {#each [['none', 'Totale'], ['group', 'Gruppi'], ['account', 'Conti']] as item}
              <button
                class:active={breakdown === item[0]}
                type="button"
                onclick={() => (breakdown = item[0] as StockQuery['breakdown'])}>{item[1]}</button
              >
            {/each}
          </div>
          <div class="segmented" aria-label="Tipo grafico">
            {#each [['area', 'Area'], ['line', 'Linea'], ['bar', 'Colonne']] as item}
              <button
                class:active={mode === item[0]}
                type="button"
                onclick={() => (mode = item[0] as ChartMode)}>{item[1]}</button
              >
            {/each}
          </div>
        </div>
      </div>
      <Chart
        option={toTimeChartOption(trend, mode)}
        ariaLabel="Andamento del patrimonio netto"
        height="24rem"
      />
    </section>

    <div class="span-7">
      <ChartCard
        title="Variazione mensile"
        description="Movimenti netti positivi e negativi."
        result={deltas}
        option={toTimeChartOption(deltas, 'bar')}
        height="19rem"
      />
    </div>
    <section class="card span-5">
      <div class="card-header">
        <div>
          <h2>Composizione finale</h2>
          <p class="muted">Per gruppo, senza compensare i segni.</p>
        </div>
      </div>
      <Chart
        option={toCompositionOption(composition)}
        ariaLabel="Composizione finale per gruppo"
        height="19rem"
      />
    </section>

    <section class="card span-12">
      <div class="card-header">
        <div>
          <h2>Prospetto mensile</h2>
          <p class="muted">Saldo iniziale, movimenti e saldo finale.</p>
        </div>
      </div>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex (scrollable region must be keyboard reachable) -->
      <div
        class="scroll-region"
        tabindex="0"
        role="region"
        aria-label="Prospetto mensile del patrimonio"
      >
        <table class="monthly-statement">
          <thead
            ><tr
              ><th>Mese</th><th>Saldo iniziale</th><th>Delta</th><th>Saldo finale</th><th
                >Variazione</th
              ></tr
            ></thead
          >
          <tbody>
            {#each rawTrend.buckets as month, index}
              {@const closing = values[index] ?? 0}
              {@const opening =
                index === 0
                  ? closing - (deltas.series[0]?.values[index] ?? 0)
                  : (values[index - 1] ?? 0)}
              {@const deltaValue = deltas.series[0]?.values[index] ?? closing - opening}
              <tr
                ><th>{formatMonth(month)}</th><td data-label="Saldo iniziale"
                  >{formatCents(opening)}</td
                ><td data-label="Delta" class={signedClass(deltaValue)}
                  >{formatCents(deltaValue)}</td
                ><td data-label="Saldo finale">{formatCents(closing)}</td><td
                  data-label="Variazione">{formatPercent(meaningfulChange(closing, opening))}</td
                ></tr
              >
            {/each}
          </tbody>
        </table>
      </div>
    </section>
  </div>
</main>
